import React, { useEffect, useRef, useState } from 'react';
import './ContactForm.css';

// Formatação de CPF/CNPJ: 000.000.000-00 ou 00.000.000/0000-00
const formatCpfCnpj = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 14);

  if (digits.length <= 11) {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }

  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

// Exibe apenas o primeiro nome, ocultando o restante por privacidade
const maskNome = (nome) => {
  const partes = (nome || '').trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '';
  return partes.length > 1 ? `${partes[0]} ***` : partes[0];
};

function ContactForm({ compact = false }) {
  const [formData, setFormData] = useState({
    nome: '', email: '', telefone: '',
    assunto: '', mensagem: '',
  });
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [protocoloOs, setProtocoloOs] = useState('');
  const erroRef = useRef(null);

  // Rola até a mensagem de erro para o cliente ver o aviso, mesmo se estiver mais abaixo na página.
  useEffect(() => {
    if (erro) {
      erroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [erro]);

  // Fluxo "Sou Cliente": CPF/CNPJ + busca automática do contrato ativo no IXC
  const [souCliente, setSouCliente] = useState(true); // null | true | false — pré-marcado por padrão
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [clienteInfo, setClienteInfo] = useState(null);
  const [contratos, setContratos] = useState([]);
  const [contratoAtivo, setContratoAtivo] = useState(null);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [erroCliente, setErroCliente] = useState('');

  // Assuntos disponíveis para a O.S., carregados diretamente do IXC
  const [assuntosOptions, setAssuntosOptions] = useState([]);

  useEffect(() => {
    let cancelado = false;

    (async () => {
      try {
        const ixcUrl = window.MSA_SEGUNDA_VIA_URL || 'http://localhost/backend-php/ixc.php';
        const res = await fetch(`${ixcUrl}?action=assuntos`);
        const data = await res.json().catch(() => ({}));
        if (!cancelado && data.success && Array.isArray(data.assuntos)) {
          setAssuntosOptions(data.assuntos);
        }
      } catch {
        // Mantém o select apenas com a opção padrão caso a busca falhe.
      }
    })();

    return () => {
      cancelado = true;
    };
  }, []);

  const getAssuntoLabel = (id) => {
    const encontrado = assuntosOptions.find((item) => String(item.id) === String(id));
    return encontrado ? `${encontrado.id} - ${encontrado.assunto}` : id;
  };

  // Busca cliente/contrato no IXC conforme o CPF/CNPJ é digitado (debounced)
  useEffect(() => {
    if (souCliente !== true) {
      return undefined;
    }

    const digits = cpfCnpj.replace(/\D/g, '');

    if (digits.length !== 11 && digits.length !== 14) {
      setClienteInfo(null);
      setContratos([]);
      setContratoAtivo(null);
      setErroCliente('');
      setBuscandoCliente(false);
      return undefined;
    }

    let cancelado = false;
    setBuscandoCliente(true);
    setErroCliente('');

    const timer = setTimeout(async () => {
      try {
        const ixcUrl = window.MSA_SEGUNDA_VIA_URL || 'http://localhost/backend-php/ixc.php';
        const res = await fetch(`${ixcUrl}?cpf_cnpj=${encodeURIComponent(digits)}`);
        const data = await res.json().catch(() => ({}));
        if (cancelado) return;

        if (data.success && data.cliente) {
          setClienteInfo(data.cliente);
          setFormData((prev) => ({ ...prev, nome: data.cliente.nome || data.cliente.razao || prev.nome }));
          const listaContratos = Array.isArray(data.contratos) ? data.contratos : [];
          const selecionado = listaContratos.find(
            (c) => String(c.id) === String(data.contrato_selecionado)
          ) || listaContratos[0] || null;
          setContratos(listaContratos);
          setContratoAtivo(selecionado);
          setErroCliente(selecionado ? '' : 'Não encontramos contrato ativo para esse CPF/CNPJ.');
        } else {
          setClienteInfo(null);
          setContratos([]);
          setContratoAtivo(null);
          setErroCliente('Não localizamos seu cadastro.');
        }
      } catch {
        if (!cancelado) {
          setClienteInfo(null);
          setContratos([]);
          setContratoAtivo(null);
          setErroCliente('Não foi possível consultar seus dados agora.');
        }
      } finally {
        if (!cancelado) setBuscandoCliente(false);
      }
    }, 600);

    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [cpfCnpj, souCliente]);

  // Só é possível abrir O.S. quando o cadastro do IXC foi realmente localizado.
  const clienteConfirmado = souCliente === true && !!clienteInfo && !!contratoAtivo;

  const handleSouClienteChange = (valor) => {
    setSouCliente(valor);
    setFormData((prev) => ({ ...prev, assunto: '' }));
    if (!valor) {
      setCpfCnpj('');
      setClienteInfo(null);
      setContratos([]);
      setContratoAtivo(null);
      setErroCliente('');
      setFormData((prev) => ({ ...prev, nome: '' }));
    }
  };

  // Formatação de Telefone: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
  const formatTelefone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Aplica formatação conforme o campo
    if (name === 'telefone') {
      formattedValue = formatTelefone(value);
    }

    setFormData({ ...formData, [name]: formattedValue });
    
    // Dispara evento formstart na primeira interação
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        'event': 'formstart',
        'event_category': 'form',
        'event_label': 'contact_form',
        'form_name': 'ContactForm'
      });
    }
  };

  const handleCpfCnpjChange = (e) => {
    setCpfCnpj(formatCpfCnpj(e.target.value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');

    // Quem se declarou cliente só pode enviar com um CPF/CNPJ válido, cadastro localizado
    // no IXC e contrato selecionado; sem isso o envio é bloqueado (não cai no fluxo de não cliente).
    if (souCliente === true) {
      if (buscandoCliente) {
        setErro('Aguarde a confirmação do CPF/CNPJ antes de enviar.');
        return;
      }
      if (!clienteConfirmado) {
        setErro(cpfCnpj ? (erroCliente || 'Selecione o contrato para enviar a solicitação.') : 'Digite o seu CPF/CNPJ para enviar a solicitação.');
        return;
      }
    }

    // Validação manual dos demais campos (o form usa noValidate para não deixar o navegador
    // barrar o envio num campo diferente antes da checagem de CPF/CNPJ acima).
    if (souCliente !== true && !formData.nome.trim()) {
      setErro('Informe seu nome completo.');
      return;
    }
    if (!formData.email.trim()) {
      setErro('Informe seu e-mail.');
      return;
    }
    if (!formData.assunto) {
      setErro('Selecione o assunto.');
      return;
    }
    if (!formData.mensagem.trim()) {
      setErro('Escreva sua mensagem.');
      return;
    }

    if (clienteConfirmado) {
      setEnviando(true);
      try {
        const ixcUrl = window.MSA_SEGUNDA_VIA_URL || 'http://localhost/backend-php/ixc.php';
        const mensagemCompleta = `Dados Preenchidos:\nE-mail: ${formData.email}\nTelefone: ${formData.telefone}\n\n${formData.mensagem}`;
        const res = await fetch(ixcUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'abrir_os',
            cpf_cnpj: cpfCnpj.replace(/\D/g, ''),
            contrato_id: contratoAtivo.id,
            id_assunto: formData.assunto,
            assunto: getAssuntoLabel(formData.assunto),
            mensagem: mensagemCompleta,
          }),
        });
        const data = await res.json().catch(() => ({}));

        if (res.ok && data.success) {
          setEnviado(true);
          setProtocoloOs(data.protocolo || data.os_id || '');
          setFormData({ nome: '', email: '', telefone: '', assunto: '', mensagem: '' });
          setCpfCnpj('');
          setClienteInfo(null);
          setContratos([]);
          setContratoAtivo(null);

          if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
              'event': 'form_submit',
              'event_category': 'form',
              'event_label': 'contact_form_os_ixc',
              'form_name': 'ContactForm'
            });
          }
        } else {
          // Nada é registrado: erro específico do IXC (ex: CPF/CNPJ não confere) é exibido ao cliente.
          setErro(data.error || 'Não foi possível abrir a solicitação. Confirme um CPF/CNPJ válido e tente novamente.');
        }
      } catch {
        setErro('Não foi possível conectar ao servidor. Tente novamente.');
      } finally {
        setEnviando(false);
      }
      return;
    }

    setEnviando(true);
    try {
      // Prioridade: 1) config.js runtime, 2) .env, 3) fallback localhost
      const apiUrl = window.MSA_API_URL || process.env.REACT_APP_API_URL || 'https://msaportapaletes.com.br/backend-php/contato.php';
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, assunto: getAssuntoLabel(formData.assunto) }),
      });
      const responseText = await res.text();
      let data = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = { erro: 'O servidor retornou uma resposta inválida.' };
      }
      if (res.ok) {
        setEnviado(true);
        setProtocoloOs('');
        setFormData({
          nome: '', email: '', telefone: '',
          assunto: '', mensagem: '',
        });
        setCpfCnpj('');
        setClienteInfo(null);
        setContratos([]);
        setContratoAtivo(null);
        
        // Envia evento de conversão para GTM
        if (typeof window !== 'undefined' && window.dataLayer) {
          window.dataLayer.push({
            'event': 'form_submit',
            'event_category': 'form',
            'event_label': 'contact_form_success',
            'form_name': 'ContactForm'
          });
        }
        
        // Redirect automático para WhatsApp após 2 segundos
        setTimeout(() => {
          const whatsappNumber = '5521987940668';
          const message = encodeURIComponent('Olá! Acabei de enviar uma mensagem pelo formulário de contato.');
          window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
        }, 2000);
      } else {
        setErro(data.erro || 'Erro ao enviar. Tente novamente.');
      }
    } catch {
      setErro('Não foi possível conectar ao servidor. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  if (enviado) {
    return (
      <div className={`contact-form ${compact ? 'contact-form--compact' : ''}`}>
        <p className="contact-form__success">
          {protocoloOs
            ? `✓ Solicitação registrada! O.S. nº ${protocoloOs} aberta no sistema. Nossa equipe entrará em contato em breve.`
            : '✓ Mensagem enviada! Nossa equipe retornará em breve.'}
        </p>
      </div>
    );
  }

  return (
    <form className={`contact-form ${compact ? 'contact-form--compact' : ''}`} onSubmit={handleSubmit} noValidate>

      <div className="contact-form__field">
        <span className="contact-form__toggle-label">Você já é cliente Faynet?</span>
        <div className="contact-form__toggle">
          <button
            type="button"
            className={`contact-form__toggle-btn ${souCliente === true ? 'contact-form__toggle-btn--active' : ''}`}
            onClick={() => handleSouClienteChange(true)}
          >
            Sou Cliente
          </button>
          <button
            type="button"
            className={`contact-form__toggle-btn ${souCliente === false ? 'contact-form__toggle-btn--active' : ''}`}
            onClick={() => handleSouClienteChange(false)}
          >
            Não sou cliente
          </button>
        </div>
      </div>

      {souCliente === true && (
        <div className="contact-form__field">
          <input
            className="contact-form__input"
            name="cpfCnpj"
            placeholder="CPF ou CNPJ *"
            inputMode="numeric"
            value={cpfCnpj}
            onChange={handleCpfCnpjChange}
          />
          {buscandoCliente && (
            <p className="contact-form__client-status contact-form__client-status--loading">
              <svg className="contact-form__spinner" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path d="M12 2a10 10 0 1 0 10 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
              Localizando seu cadastro...
            </p>
          )}
          {!buscandoCliente && clienteInfo && contratoAtivo && (
            <p className="contact-form__client-status contact-form__client-status--success">
              Olá, {maskNome(clienteInfo.nome || clienteInfo.razao)}! Encontramos seu cadastro.
            </p>
          )}
          {!buscandoCliente && erroCliente && (
            <p className="contact-form__client-status contact-form__client-status--error">
              {erroCliente}
            </p>
          )}
        </div>
      )}

      {souCliente === true && clienteInfo && contratos.length > 0 && (
        <div className="contact-form__field">
          <span className="contact-form__toggle-label">Sobre qual contrato você quer falar?</span>
          <div className="contact-form__contracts">
            {contratos.map((contrato) => (
              <label key={contrato.id} className="contact-form__contract-option">
                <input
                  type="checkbox"
                  checked={String(contratoAtivo?.id) === String(contrato.id)}
                  onChange={() => setContratoAtivo(contrato)}
                />
                <span className="contact-form__contract-info">
                  <span className="contact-form__contract-name">
                    {contrato.contrato || `Contrato nº ${contrato.id}`}
                  </span>
                  {contrato.endereco && (
                    <span className="contact-form__contract-address">{contrato.endereco}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {souCliente !== true && (
        <div className="contact-form__field">
          <input 
            className="contact-form__input" 
            name="nome" 
            placeholder="Nome completo *"
            required 
            value={formData.nome} 
            onChange={handleChange} 
          />
        </div>
      )}

      <div className="contact-form__field">
        <input 
          className="contact-form__input" 
          name="email" 
          type="email"
          placeholder="E-mail *"
          required 
          value={formData.email} 
          onChange={handleChange} 
        />
      </div>

      <div className="contact-form__field">
        <input 
          className="contact-form__input" 
          name="telefone" 
          placeholder="Telefone (00) 00000-0000"
          value={formData.telefone} 
          onChange={handleChange} 
        />
      </div>

      <div className="contact-form__field">
        <select 
          className="contact-form__input contact-form__select" 
          name="assunto" 
          required 
          value={formData.assunto} 
          onChange={handleChange}
        >
          <option value="">Selecione o assunto *</option>
          {souCliente === true ? (
            assuntosOptions.map((item) => (
              <option key={item.id} value={item.id}>{`${item.id} - ${item.assunto}`}</option>
            ))
          ) : (
            <>
              <option>Orçamento</option>
              <option>Dúvida técnica</option>
              <option>Parceria</option>
              <option>Reclamação</option>
              <option>Outro</option>
            </>
          )}
        </select>
      </div>

      <div className="contact-form__field">
        <textarea 
          className="contact-form__input contact-form__textarea" 
          name="mensagem" 
          placeholder="Mensagem *"
          required 
          rows={4} 
          value={formData.mensagem} 
          onChange={handleChange} 
        />
      </div>

      <button
        type="submit"
        className="contact-form__btn"
        disabled={enviando || (souCliente === true && buscandoCliente)}
      >
        {enviando ? 'Enviando...' : (clienteConfirmado ? 'Abrir Solicitação' : 'Enviar Mensagem')}
      </button>

      {erro && <p className="contact-form__error" ref={erroRef}>{erro}</p>}
    </form>
  );
}

export default ContactForm;

